<template>
  <div data-grm-comp="TestbankTable">
    <div class="h-full flex-col hidden lg:flex">
      <table class="test-bank-table">
        <colgroup>
          <col width="4%">
          <col width="20%">
          <col width="6%">
          <col width="8%">
          <col width="6%">
        </colgroup>
        <thead class="h-8 bg-gray-200">
          <tr>
            <th>{{ $t("common.number") }}</th>
            <th>{{ $t("setting.name.title") }}</th>
            <th>{{ $t("testBank.number.paper") }}</th>
            <th>{{ $t("link.regDate") }}</th>
            <th></th>
          </tr>
        </thead>
      </table>
      <div class="max-h-full overflow-y-auto scrollbar-hidden">
        <table class="test-bank-table">
          <colgroup>
            <col width="4%">
            <col width="20%">
            <col width="6%">
            <col width="8%">
            <col width="6%">
          </colgroup>
          <tbody>
            <template>
              <tr v-for="(testbank, i) in testbankList.content"
                  :key="testbank.bankId"
                  class="cursor-pointer transition hover:bg-gray-100"
                  @click="onSelectTestbank(testbank)"
              >
                <td>{{ isEditTestbank(testbank) ? '-' : testbankNum(i) }}</td>
                <td>
                  <template v-if="isEditTestbank(testbank)">
                    <GrmTextField :value="testbank.bankTitle"
                                  ref="testbankTitle"
                                  size="xs"
                                  textalign="center"
                                  @keydown.esc="onCancelTestbank($event, testbank)"
                                  @keydown.enter="onCreateTestbank($event, testbank)"
                                  @input="onChangeTestbankTitle"
                    />
                  </template>
                  <template v-else>{{ testbank.bankTitle }}</template>
                </td>
                <td>{{ testbank.bankExamSize ?? '-' }}</td>
                <td>{{ testbank.regDate ?? '-' }}</td>
                <td>
                  <div v-if="isEditTestbank(testbank)" class="w-full flex justify-center gap-x-1">
                    <GrmBtn size="xs" @click="onCreateTestbank($event, testbank)">{{ $t("common.register") }}</GrmBtn>
                    <GrmBtn size="xs" color="red" @click="onCancelTestbank($event, testbank)">{{ $t("common.cancel") }}</GrmBtn>
                  </div>
                  <GrmBtn v-else size="xs" color="red" @click="onDeleteTestbank($event, testbank)">{{ $t("common.delete") }}</GrmBtn>
                </td>
              </tr>
              <tr v-if="!testbankList.content?.length">
                <td colspan="5" class="text-center">{{ $t("testBank.notExist") }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div class="flex flex-col gap-2 lg:hidden">
      <div v-for="(testbank, i) in testbankList.content"
           :key="testbank.bankId"
           class="p-2 rounded bg-gray-100"
      >
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("common.number") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ isEditTestbank(testbank) ? '-' : testbankNum(i) }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("setting.name.title") }}</div>
          <div class="min-w-0 flex-1 py-1 px-3 text-sm lg:py-2">
            <template v-if="isEditTestbank(testbank)">
              <GrmTextField v-model="testbank.bankTitle"
                            ref="testbankTitle"
                            size="xs"
                            @keydown.esc="onCancelTestbank($event, testbank)"
                            @keydown.enter="onCreateTestbank($event, testbank)"
              />
            </template>
            <template v-else>{{ testbank.bankTitle }}</template>
          </div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.number.paper") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ testbank.bankExamSize ?? '-' }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("link.regDate") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ testbank.regDate ?? '-' }}</div>
        </div>
        <div class="py-2 px-3 flex justify-center">
          <div v-if="isEditTestbank(testbank)" class="w-full flex justify-center gap-x-1">
            <GrmBtn class="flex-1" size="sm" @click="onCreateTestbank($event, testbank)">{{ $t("common.register") }}</GrmBtn>
            <GrmBtn class="flex-1" size="sm" color="red" @click="onCancelTestbank($event, testbank)">{{ $t("common.cancel") }}</GrmBtn>
          </div>
          <div v-else class="w-full flex gap-x-1">
            <GrmBtn class="flex-1" size="sm" @click="onSelectTestbank(testbank)">{{ $t('common.see') }}</GrmBtn>
            <GrmBtn class="flex-1" size="sm" color="red" @click="onDeleteTestbank($event, testbank)">{{ $t("common.delete") }}</GrmBtn>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!testbankList?.content?.length" class="lg:hidden">
        <p class="text-center p-2 rounded bg-gray-100 text-sm">{{ $t("testBank.notExist") }}</p>
    </div>
    <GrmPagination class="mt-3"
                   :pageInfo="testbankList.pageInfo"
                   @paging="(page) => { getTestbankList({ page }) }"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";

import GrmBtn from "@/components/Common/button/GrmBtn.vue";
import GrmTextField from "@/components/Common/textField/GrmTextField.vue";
import GrmPagination from "@/components/Common/pagination/GrmPagination.vue";

export default {
  name: 'TestbankTable',
  components: {
    GrmBtn,
    GrmTextField,
    GrmPagination,
  },
  data() {
    return {
      currentPage: 1,
    }
  },
  computed: {
    ...mapState('testbank', [
      'testbankList',
      "testbankListFilter",
    ]),
    ...mapGetters('room', [
      'room',
    ]),

    isEditTestbank() {
      return (testbank) => testbank.type === 'edit';
    },
    hasEdit() {
      return this.testbankList.content?.some(this.isEditTestbank);
    },
    testbankNum() {
      return (i) => {
        return this.testbankList.pageInfo.totalCount - ((this.testbankList.pageInfo.currentPageNum - 1) * this.testbankListFilter.size + (this.hasEdit ? i - 1 : i));
      }
    },
  },
  watch: {
    hasEdit(val) {
      if (!val) {
        return;
      }

      this.$nextTick(() => {
        for (let i = 0; i < this.$refs.testbankTitle.length; i++) {
          const $el = this.$refs.testbankTitle[i].$el;
          const visible = $el.offsetWidth > 0 && $el.offsetHeight > 0;
          if (visible) {
            this.$refs.testbankTitle[i].$refs?.input.focus();
            break;
          }
        }
      });
    },
  },
  created() {
    this.getTestbankList({
      inThisRoom: this.testbankListFilter.inThisRoom,
      roomId: this.testbankListFilter.inThisRoom ? this.room.roomId : ''
    });
  },
  methods: {
    ...mapMutations('testbank', [
      'SET_VIEW',
      'SET_TESTBANK',
      'DELETE_TESTBANK',
      'SET_TESTBANK_LIST',
    ]),
    ...mapActions('testbank', [
      'createTestbank',
      'deleteTestbank',
      'getTestbankList',
    ]),

    async onCreateTestbank(e, testbank) {
      e.stopPropagation();

      if (!testbank.bankTitle.trim()) {
        this.$alert(this.$t('testBank.alert.name'));
        return;
      }

      try {
        await this.createTestbank({
          bankId: testbank.bankId,
          bankTitle: testbank.bankTitle,
          roomId: this.room.roomId
        });
        this.$alert(this.$t('testBank.alert.regComplete'));
      } catch (e) {
        this.$alert(this.$t('testBank.alert.regFail'));
      }
    },
    onCancelTestbank(e, testbank) {
      e.stopPropagation();

      this.DELETE_TESTBANK({
        bankId: testbank.bankId,
      });
    },
    onDeleteTestbank(e, testbank) {
      e.stopPropagation();

      this.$alert({
        text: this.$t('testBank.alert.delete'),
        ok: () => {
          this.deleteTestbank({
            bankId: testbank.bankId,
          });
        }
      })
    },
    onSelectTestbank(testbank) {
      if (testbank.type === 'edit') {
        return;
      }

      this.SET_TESTBANK(testbank);
      this.SET_VIEW('testbank__exam_list');
    },
    onChangeTestbankTitle(val) {
      this.SET_TESTBANK_LIST({
        ...this.testbankList,
        content: this.testbankList.content.map(testbank => {
          if (testbank.type === 'edit') {
            return {
              ...testbank,
              bankTitle: val,
            }
          }
          return testbank;
        })
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.test-bank-table {
  width: 100%;
  font-size: 13px;
  text-align: center;
  table-layout: fixed;
}
tr {
  @apply h-10 border-b border-gray-100 whitespace-nowrap;
}
th {
  @apply px-3 text-xs whitespace-nowrap
}
td {
  @apply px-3 text-xs truncate whitespace-nowrap
}
</style>